import {useLegacyGrpcRequest} from '@aboutyou/data-fetcher';
import {CheckoutV1_getCheckoutUrlLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {CheckoutURLV1Request} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.types';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {Log} from '@aboutyou/mlm';
import {useSimpleUserTokenOrSessionId} from '@aboutyou/user';
import {useEffect, useRef, useState} from 'react';
import {useRequestSnapshotCampaignKey} from '../../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscount';
import {usePrepareCheckout} from '../../../backend-services/Checkout/hooks/usePrepareCheckout';
// eslint-disable-next-line monorepo/no-internal-import
import {useGrpcClientConfig} from '@aboutyou/data-fetcher/src/grpc/GrpcClientContext';
import {useCheckoutEntryDetails} from './useCheckoutEntryDetails';

type MakeAllKeysRequired<T> = {
  [K in keyof T]-?: T extends Record<K, T[K]> ? T[K] : T[K] | null;
};

export const useGetCheckoutUrlRequestOptions = ({
  entryTimestamp,
}: {
  entryTimestamp: number;
}): MakeAllKeysRequired<
  Omit<CheckoutURLV1Request, 'childShopId' | 'device'>
> => {
  const tokenOrSessionId = useSimpleUserTokenOrSessionId();
  const campaignKeySnapshot = useRequestSnapshotCampaignKey();
  const {abTests} = useGrpcClientConfig();

  let authToken = '';

  if (tokenOrSessionId && 'token' in tokenOrSessionId) {
    authToken = tokenOrSessionId.token;
  }

  return {
    session: {
      authToken,
    },
    campaignKey: {value: (campaignKeySnapshot?.campaignKey || '').toString()},
    selectedDiscount: {
      $case: 'noDiscount',
      noDiscount: {},
    },
    voucher: null,
    correlationId: null,
    basketTotal: null,
    expressProvider: null,
    rawConsentConfig: null,
    abTestConfig: {abTests},
    timestamp: new Date(entryTimestamp),
  };
};

export const useGetCheckoutUrlRequest = ({
  entryTimestamp,
}: {
  entryTimestamp: number;
}) => {
  const hydrationDone = useHydrationDone();
  const options = useGetCheckoutUrlRequestOptions({entryTimestamp});

  const campaignKeySnapshot = useRequestSnapshotCampaignKey();
  const prepareCheckout = usePrepareCheckout();

  const hasSession = Boolean(options.session.authToken);

  const [config, setConfig] = useState({disabled: true});
  const errorLogged = useRef(false);

  useEffect(() => {
    if (errorLogged.current) return;

    const token = options.session?.authToken;

    if (config.disabled === false) {
      // when one of these required properties are empty, the CO page goes into a infinite loading state.
      // this log is to catch those cases
      if (!token) {
        Log.error('@aboutyou/logic-components', 'CheckoutErrors', {
          error_message:
            'getCheckoutUrl called without required checkout session data',
          noToken: !!token,
          code: -1,
        });

        errorLogged.current = true;
      }
    }
  }, [config.disabled, options.session?.authToken]);

  /**
   * Prepare the Checkout CampaignKey if its not done already!
   */
  useEffect(() => {
    if (campaignKeySnapshot.isLoaded) return;

    prepareCheckout().catch(error => {
      Log.error('@aboutyou/logic-components', 'CheckoutErrors', {
        error_message: 'Prepare Checkout Step failed',
        error,
      });
      /**
       * Allow CO to be loaded even if the CampaignKey Prepare fails
       */
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setConfig({disabled: false});
    });
  }, [campaignKeySnapshot.isLoaded, prepareCheckout]);

  /**
   * Wait until these steps are completed before the request is enabled:
   * 1. Hydration is done
   * 2. Session is loaded
   * 3. CampaignKey is loaded
   */
  useEffect(() => {
    if (!campaignKeySnapshot.isLoaded) return;

    if (!hydrationDone) return;

    if (!hasSession) return;

    // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
    setConfig({disabled: false});
  }, [hydrationDone, hasSession, campaignKeySnapshot.isLoaded]);

  const request = useLegacyGrpcRequest(
    CheckoutV1_getCheckoutUrlLazy,
    {
      ...options,
      checkoutSession: null,
    },
    {
      ...config,
      // we need to refetch the checkout url on every CO page load, because the entry url parameters (accessToken, hash, entryTime, etc) can change on every entry to the CO page
      cacheTime: 0,
      // when this endpoint fails, retrying it just increase the checkout-entry attempts in the monitoring, we already handle the error code:
      // - to show error toast
      // - to show the login modal
      // - to redirect the user to the basket page
      retry: false,
      onSuccess: response => {
        Log.unSampledLog('GetCheckoutUrl-CallParams', {
          ...options,
          status: 'success',
          requestCampaignKey: options.campaignKey?.value || 'NOT-SET',
          responseCampaignKey: response.campaignKey || 'NOT-SET',
        });
      },
      onError: error => {
        Log.unSampledLog('GetCheckoutUrl-CallParams', {
          ...options,
          requestCampaignKey: options.campaignKey?.value || 'NOT-SET',
          status: 'error',
          response: error,
        });
      },
    },
  );

  const entryDetails = useCheckoutEntryDetails({
    jwt: request.data?.jwt,
  });

  return {
    ...request,
    data: request.data
      ? {
          ...request.data,
          entryDetails,
        }
      : undefined,
  };
};
